import {
  Button,
  DynamicContainer,
  Form,
  FormContext,
  FormControl,
  FormGroup,
  FormLabel,
  Icon,
  IconButton,
  SelectOption,
} from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import classNames from 'classnames';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { SDIType, SdiCheckbox } from 'src/js/component/base/form/sdi-checkbox';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import systemInfo from 'src/js/data/systemInfo';
import { triggerEvent, watchEvent } from 'src/js/events';
import { keyValueForEnum } from 'src/js/helper/enum-helper';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { StillImagesPickupModal } from 'src/js/pages/settings/stillimages/stillimages-pickup-modal';
import streamModels from 'src/js/pages/streaming/stream/stream-store';
import { RebootNeeded, RebootNeededRef } from 'src/js/reboot/reboot-needed';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { validateFormContext } from 'src/js/validator/validator';
import { decoderOptionsStreamName } from '../stream/stream-view-model-mapper';

import {
  BufferingModeEnum,
  DecoderHdrEnum,
  DecoderViewModel,
  MkRange,
  QuadModeEnum,
  StillImagesEnum,
  decoderValidationSchema,
  fixedDelayRange,
  multiSyncDelayRange,
  multiSyncWarning,
  outputFrameRateOptions,
} from './decoder-model';
import decoders, { updateDecoder } from './decoder-store';
import { generateDataAuto } from 'src/js/util/automation';
import { changes } from 'src/js/util/formik-helper';
import { useWatchObject } from 'src/js/store/use-watch';
import { HaiUIFormSubmitWorkaround } from 'src/js/util/haiui-wokraorund-types';

interface Props {
  model: DecoderViewModel;
  onCancel: VoidFunction;
}

export const CompanionField = (props: any) => {
  return (
    <div className="d-flex mk-companioned-field">
      {props.children}
      {props.companion}
    </div>
  );
};
CompanionField.displayName = 'CompanionField';

export const DecoderEdit: React.FunctionComponent<Props> = ({ model, onCancel }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const formRef = useRef<FormikProps<DecoderViewModel>>();
  const rebootNeededRef = useRef<RebootNeededRef>(null);

  const [triggerReset, setTriggerReset] = useMutable(false);

  // setting a state here prevents live update from modifying the edit form
  const [decoder, setDecoder] = useState<DecoderViewModel>(model);
  watchEvent('preset-load', () => {
    // at this point decoders has usually not been fetched yet.
    setTriggerReset(true);
  });

  // R4KD-1976 - When loading a preset, we need to update the edit form if it's opened.
  useWatchObject(decoders, (_updated) => {
    if (triggerReset()) {
      setTriggerReset(false);
      const dec = decoders.find((e) => e.id === model.id);
      setDecoder(dec); // update edit view with newest model
      formRef.current.resetForm({ values: dec });
    } else {
      // update only stats
      const dec = decoders.find((e) => e.id === model.id);
      if (dec) {
        setDecoder((prev) => ({
          ...prev,
          stats: dec.stats,
        }));
      }
    }
  });

  if (decoder.streamId !== model.streamId) {
    // if another interface like CLI loads a preset, we might will detect it here if the IDs changed.
    setDecoder(model);
    triggerEvent('preset-load');
  }

  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);

  const navigate = useNavigate();
  const handleClickOpenStreamEdit = (streamId: number) => {
    navigate('/streaming/stream/', { state: { id: streamId, stats: false } });
  };

  const handleSubmit = (values: DecoderViewModel) => {
    setButtonState('pending');

    const data = { ...values };
    data.stillImage = Number(data.stillImage);
    if (data.stillImage === StillImagesEnum.CUSTOM && isNilOrEmpty(values.stillImageFilename)) {
      data.stillImage = undefined;
    }

    data.state = undefined; // R4KD-2751 - Fixes "Changing Still Image on Stopped Decoder Forces Restart". We shouldn't reapply the old state when the panel was openned because it may have been started/stopped since.

    return updateDecoder(data, t).then((res) => {
      if (res.ok) {
        const dec = decoders.find((e) => e.id === model.id);
        setDecoder(dec); // update edit view with newest model
        formRef.current.resetForm({ values: dec });
        triggerEvent('preset-update');
        triggerEvent('thumbnail-update');
      }
      setButtonState(undefined);
      return res;
    });
  };

  const isQuadMode2SI = (decoder: DecoderViewModel, quadMode: QuadModeEnum) =>
    decoder?.outputs.every((output) => output) && quadMode === QuadModeEnum.QUAD_2SI;
  const [quadMode2SI, setQuadMode2SI] = useState(isQuadMode2SI(decoder, decoder.quadMode));
  const handleChangeQuadMode = (event: any) => {
    setQuadMode2SI(isQuadMode2SI(formRef.current.values, event.target.value));
  };

  const handleChangeSDIOutput = (event: any) => {
    const index = event.target.dataset.index;
    const outputs = [...formRef.current.values.outputs];

    if (systemInfo.personality === '2110') {
      // unselect others
      for (let i = 0; i < decoder.outputs.length; i++) {
        outputs[i] = false; // R4KD-2378
      }
    }

    outputs[index] = event.target.checked;

    const newQuadMode2SI =
      outputs.every((output) => output) && formRef.current.values.quadMode == QuadModeEnum.QUAD_2SI;
    setQuadMode2SI(newQuadMode2SI);

    formRef.current.setFieldValue('outputs', outputs);
    formRef.current.setFieldTouched('outputs');
  };

  const [stillImagePrev, setStillImagePrev] = useMutable(null);
  const [stillImagePickupShown, showStillImagePickup, hideStillImagePickup] = useModalControls();
  const handleChangeStillImage = (event: any) => {
    if (Number(event.target.value) === StillImagesEnum.CUSTOM) {
      setStillImagePrev(Number(formRef.current.values.stillImage));
      showStillImagePickup();
    }
    formRef.current.setFieldValue('stillImage', Number(event.target.value)); // this is almost useless because HaiUI will set it in formik after our callback is called. TODO: Open a bug in HaiUI
    formRef.current.setFieldTouched('stillImage');
  };

  const onSelectStillImage = (stillImage: string) => {
    formRef.current.setFieldValue('stillImage', StillImagesEnum.CUSTOM);
    formRef.current.setFieldValue('stillImageFilename', stillImage);
    formRef.current.setFieldTouched('stillImage');
  };

  const handleCancelStillImage = () => {
    hideStillImagePickup();
    const stillImageFilename = formRef.current.values.stillImageFilename;
    if (stillImagePrev() !== StillImagesEnum.CUSTOM) {
      formRef.current.setFieldValue('stillImage', stillImagePrev());
    } else {
      formRef.current.setFieldValue('stillImage', StillImagesEnum.CUSTOM);
      formRef.current.setFieldValue('stillImageFilename', '');
      setTimeout(() => {
        // R4KD-2408 workaround for bug in HaiUI or Formik (unreported)
        formRef.current.setFieldValue('stillImage', StillImagesEnum.CUSTOM);
        formRef.current.setFieldValue('stillImageFilename', stillImageFilename);
        formRef.current.setFieldTouched('stillImage');
      }, 1);
    }
    formRef.current.setFieldTouched('stillImage');
  };

  const multiSyncValidationRange: MkRange = multiSyncDelayRange(decoder.stats);
  const fixedDelayValidationRange: MkRange = fixedDelayRange(decoder.stats, decoder.bufferingMode);

  // stream options
  let streamOptions: { value: string | number; label: string }[] = [
    { value: -1, label: t('decoder.streamSelectNone') },
  ];

  // for react-select if we ever use it again
  //let selectedStreamOption = streamOptions[0];
  //let selectedStreamOptionValue = '-1';

  streamOptions = streamOptions.concat(
    streamModels.map((stream) => {
      const option = {
        value: stream.id,
        label: decoderOptionsStreamName(t, stream),
      };
      /*/ for react-select if we ever use it again
      if (decoder.streamId === stream.id) {
      //  selectedStreamOption = option;
          selectedStreamOptionValue = stream.id.toString();
      }*/
      return option;
    }),
  );

  const [firstTime, setFirstTime] = useMutable(true);

  const recommendedDelayFunc = () => {
    return ''; /* this below was used when we were combining recommended range with valid range, but we dont do that anymore so this always return empty string R4KD-2519
    let min = -1;
    let max = -1;
    let recommendedDelay = '';

    const bufferingMode = formRef.current?.values?.bufferingMode ?? decoder.bufferingMode;
    if (bufferingMode === BufferingModeEnum.FIXED) {
      min = fixedDelayValidationRange.min;
      max = fixedDelayValidationRange.max <= 3000 ? fixedDelayValidationRange.max : 3000;
    } else {
      min = multiSyncValidationRange.min;
      max = multiSyncValidationRange.max;
    }
    if (bufferingMode !== decoder.bufferingMode) {
      // we are changing buffering mode, so ignore the recommended delay from backend which is not accurate
      (min = 0), (max = bufferingMode === BufferingModeEnum.MULTISYNC ? 10000 : 3000);
    }

    // only show recommendation if different from hard validation range.
    if (bufferingMode === BufferingModeEnum.FIXED) {
      if (min !== 0 || max !== 3000) {
        recommendedDelay = t('validation.decoder.recommendedDelay', {
          min,
          max,
        });
      }
    } else if (bufferingMode === BufferingModeEnum.MULTISYNC) {
      if (min !== 0 || max !== 10000) {
        recommendedDelay = t('validation.decoder.recommendedDelay', {
          min,
          max,
        });
      }
    }

    return recommendedDelay;*/
  };

  return (
    <>
      <Form
        initialValues={decoder}
        defaultValidation={true}
        handleSubmit={handleSubmit as unknown as HaiUIFormSubmitWorkaround}
        className="hai-mt-n8"
        restValidationProps={{
          enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
          validationSchema: decoderValidationSchema(
            multiSyncValidationRange,
            fixedDelayValidationRange,
            recommendedDelayFunc,
            t,
          ),
          innerRef: formRef,
        }}
      >
        <FormContext.Consumer>
          {(genericContext) => {
            const formContext: FormikProps<DecoderViewModel> = genericContext as any;
            const values = formContext.values;

            if (firstTime()) {
              formContext.setFieldTouched('bufferingDelay', true);
              setFirstTime(false);
            }

            let applyButtonEnabled = formContext.dirty;
            if (applyButtonEnabled) {
              const changed = changes(values, formContext.initialValues);
              if (changed.stillImage !== undefined) {
                if (values.stillImage == formContext.initialValues?.stillImage) {
                  delete changed.stillImage; // string vs integer
                }
              }
              // final calculation
              if (Object.keys(changed).length === 0) {
                applyButtonEnabled = false;
              }
            }

            const fixedRange = fixedDelayRange(decoder.stats, values.bufferingMode);
            const showFixedBufferingOutOfRange =
              values.bufferingMode === BufferingModeEnum.FIXED &&
              (values.bufferingDelay < fixedRange.min || values.bufferingDelay > fixedRange.max);

            const multiSyncRange = multiSyncDelayRange(decoder.stats);
            const showMultiSyncOutOfRange =
              values.bufferingMode === BufferingModeEnum.MULTISYNC &&
              (values.multisyncBufferingDelay < multiSyncRange.min ||
                values.multisyncBufferingDelay > multiSyncRange.max);

            const companionField = (children: any) => {
              const disabled = values.streamId == -1;
              return (
                <CompanionField>
                  {children}
                  <div className="box mk-icon-action">
                    <IconButton
                      data-auto={generateDataAuto('stream', 'icon')}
                      onClick={() => handleClickOpenStreamEdit(values.streamId)}
                      title={t('dashboard.Stats')}
                      disabled={disabled}
                    >
                      <Icon iconname="Streams" />
                    </IconButton>
                  </div>
                </CompanionField>
              );
            };

            return (
              <>
                <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                  <FormTextfield
                    name="name"
                    mask
                    placeholder={t('decoder.name', { id: decoder.id })}
                    title={t('general.name')}
                    className="max-width"
                  />
                  {!sessionStore.isUser() && (
                    <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                      <Button className="hai-mr-6" onClick={onCancel}>
                        {t('general.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => validateFormContext(formContext, t)}
                        disabled={!applyButtonEnabled}
                        state={buttonState}
                      >
                        {t('general.apply')}
                      </Button>
                    </div>
                  )}
                </DynamicContainer>
                <FormBuilder
                  translationPrefixLabels="decoder.fields"
                  translationPrefixEnums="decoder.enums"
                  layout={FormLayout.THREE_COLUMNS_GRID}
                  fields={[
                    {
                      type: FormFieldType.TITLE,
                      label: t('decoder.sections.stream'),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      mask: true,
                      name: 'streamId',
                      //    value: selectedStreamOptionValue,
                      options: streamOptions,
                      container: companionField,
                    },
                    {
                      type: FormFieldType.NODE,
                      node: (
                        <FormGroup className="sdi-checkbox-container">
                          <FormLabel>
                            {systemInfo.personality === '2110'
                              ? t('dashboard.st2110Outputs')
                              : t('dashboard.sdiOutputs')}
                          </FormLabel>
                          <div className={classNames('sdi-checkboxes hai-mt-1', quadMode2SI && 'quadMode2SI')}>
                            {values.outputs.map((value, index) => {
                              const type =
                                value !== values.outputsOrig[index] && value == true
                                  ? SDIType.HIGHLIGHT
                                  : value !== values.outputsOrig[index] && value == false
                                  ? SDIType.OUTLINE
                                  : SDIType.NORMAL;
                              return (
                                <SdiCheckbox
                                  key={index}
                                  name={`outputs[${index}]`}
                                  data-index={index}
                                  checked={value}
                                  type={type}
                                  readonly={systemInfo.personality === '2110'}
                                  onChange={handleChangeSDIOutput}
                                >{`${index + 1}`}</SdiCheckbox>
                              );
                            })}
                          </div>
                        </FormGroup>
                      ),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'quadMode',
                      options: keyValueForEnum(QuadModeEnum).map(([, value]) => ({
                        label: t(`decoder.enums.quadMode.${value}`),
                        value: value,
                      })),
                      onChange: handleChangeQuadMode,
                      when: values.outputs.every((output) => output),
                    },
                    {
                      type: FormFieldType.TITLE,
                      label: t('decoder.sections.input'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'resolution',
                      value: model.stats.videoInputResolution,
                    },
                    {
                      type: FormFieldType.LINE_BREAK,
                    },
                    /* removed in 1.6 R4KD-1974
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'audio',
                      value: model.stats.audioSampleRate,
                    },*/
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'bufferingMode',
                      options: [
                        {
                          value: BufferingModeEnum.FIXED,
                          label: t(`decoder.bufferingModes.FIXED`),
                        },
                        {
                          value: BufferingModeEnum.MULTISYNC,
                          label: t(`decoder.bufferingModes.MULTISYNC`),
                        },
                        {
                          value: BufferingModeEnum.AUTOMATIC,
                          label: t(`decoder.bufferingModes.AUTOMATIC`),
                        },
                      ],
                      addon: (
                        <>
                          {values.bufferingMode === BufferingModeEnum.MULTISYNC && (
                            <div className="warning-container" id="decoder-multisync-warning">
                              {multiSyncWarning(values.stats, t)}
                            </div>
                          )}
                        </>
                      ),
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'bufferingDelay',
                      hint: t('validation.decoder.recommendedDelay', {
                        min: fixedDelayValidationRange.min,
                        max: fixedDelayValidationRange.max,
                      }),
                      when: values.bufferingMode === BufferingModeEnum.FIXED,
                      alwaysShowHint:
                        !formContext.errors.bufferingDelay && values.bufferingDelay > fixedDelayValidationRange.max,
                      className: showFixedBufferingOutOfRange && !formContext.errors.bufferingDelay && 'warningHint',
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'multisyncBufferingDelay',
                      hint:
                        decoder.stats?.multisyncDelayRangeMaxMs > 0 &&
                        (values.multisyncBufferingDelay > 10000
                          ? t('validation.decoder.bufferingDelay', { recommended: '', min: 0, max: 10000 })
                          : t('validation.decoder.recommendedDelay', {
                              min: multiSyncValidationRange.min,
                              max: multiSyncValidationRange.max,
                            })),
                      when: values.bufferingMode === BufferingModeEnum.MULTISYNC,
                      alwaysShowHint:
                        !formContext.errors.multisyncBufferingDelay &&
                        values.multisyncBufferingDelay > multiSyncValidationRange.max,
                      className:
                        showMultiSyncOutOfRange && !formContext.errors.multisyncBufferingDelay && 'warningHint',
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'dropCorruptedFrames',
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'clockTracking',
                    },
                    {
                      type: FormFieldType.TITLE,
                      label: t('decoder.sections.output'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'outputResolution',
                      value: model.stats.videoDisplayResolution,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'outputFrameRate',
                      options: outputFrameRateOptions(t).map((fr) => ({
                        label: fr.label,
                        value: fr.value,
                      })),
                    },
                    /*{
                      type: FormFieldType.CHECKBOX,
                      name: 'audioTrackingPrecision',
                    },*/
                    {
                      type: FormFieldType.LINE_BREAK,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'hdrDynamicRange',
                      options: keyValueForEnum(DecoderHdrEnum).map(([, value]) => ({
                        label: t(`decoder.enums.hdr.${value}`),
                        value: value,
                      })),
                      when: systemInfo.hasHDR === true,
                    },
                    {
                      type: FormFieldType.NODE,
                      node: (
                        <div className={`mk-form-select`}>
                          <FormGroup>
                            <FormLabel>{t('decoder.fields.stillImage')}</FormLabel>
                            <FormControl
                              name="stillImage"
                              as="select"
                              defaultSelect={
                                // R4KD-2046 the logic is complex with this one to workaround HaiUI behavior
                                values.stillImage == StillImagesEnum.CUSTOM ? values.stillImageFilename : undefined
                              }
                              defaultValue={values.stillImage == StillImagesEnum.CUSTOM ? undefined : values.stillImage}
                              onChange={handleChangeStillImage}
                            >
                              {keyValueForEnum(StillImagesEnum).map(([, value]) => {
                                // using value.toString() is kind of a hack. otherwise defaultSelect wont work rendering the custom filename
                                return (
                                  <SelectOption
                                    key={value}
                                    value={value === StillImagesEnum.CUSTOM ? value.toString() : value}
                                  >
                                    {t(`decoder.enums.stillImage.${value}`)}
                                  </SelectOption>
                                );
                              })}
                            </FormControl>
                          </FormGroup>
                        </div>
                      ),
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'stillImageDelay',
                      hint: t('decoder.fields.stillImageDelayExplain'),
                      required: true,
                    },

                    {
                      type: FormFieldType.TITLE,
                      label: t('decoder.sections.metadata'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'klv',
                      value: model.stats?.klvUnlicensed
                        ? t('decoder.metadata.unlicensed')
                        : model.stats?.klv && model.stats?.klvLastSeen != null && model.stats?.klvLastSeen != 'Never'
                        ? `${t('statistics.lastSeen')} ${model.stats?.klvLastSeen}`
                        : t('decoder.metadata.notPresent'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'cc',
                      value:
                        model.stats?.closedCaption &&
                        model.stats?.ccLastSeen != null &&
                        model.stats?.ccLastSeen != 'Never'
                          ? `${t('statistics.lastSeen')} ${model.stats?.ccLastSeen}`
                          : t('decoder.metadata.notPresent'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'tc',
                      value:
                        model.stats?.timeCode && model.stats?.tcLastSeen != null && model.stats?.tcLastSeen != 'Never'
                          ? `${t('statistics.lastSeen')} ${model.stats?.tcLastSeen}`
                          : t('decoder.metadata.notPresent'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'afd',
                      value:
                        model.stats?.afd && model.stats?.afdLastSeen != null && model.stats?.afdLastSeen != 'Never'
                          ? `${t('statistics.lastSeen')} ${model.stats?.afdLastSeen}`
                          : t('decoder.metadata.notPresent'),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'scte',
                      value:
                        model.stats?.scte && model.stats?.scteLastSeen != null && model.stats?.scteLastSeen != 'Never'
                          ? `${t('statistics.lastSeen')} ${model.stats?.scteLastSeen}`
                          : t('decoder.metadata.notPresent'),
                    },
                  ]}
                />
              </>
            );
          }}
        </FormContext.Consumer>
      </Form>
      {stillImagePickupShown && (
        <StillImagesPickupModal
          onSelect={(imageName: string) => {
            hideStillImagePickup();
            return onSelectStillImage(imageName);
          }}
          onCancel={handleCancelStillImage}
          onClose={(e: any) => {
            if (e?.target?.className?.toString().includes('primary')) {
              hideStillImagePickup();
            } else {
              handleCancelStillImage(); // X button or click outside the modal
            }
          }}
          selected={formRef.current?.values?.stillImageFilename ?? decoder.stillImageFilename}
        ></StillImagesPickupModal>
      )}
      <RebootNeeded ref={rebootNeededRef} />
    </>
  );
};
